import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { ShoppingCart, Store, HelpCircle } from "lucide-react";

const FaqPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  const faqData = [
    {
      section: "General Questions",
      icon: HelpCircle,
      questions: [
        {
          q: "What is PartsLokator?",
          a: "We’re an AI-powered marketplace that connects customers with businesses selling recycled and used auto parts. Buyers can search by make, model, year, and part type—or ask our AI assistant for help."
        },
        {
          q: "How do I find the part I need?",
          a: "Use the search bar or chat with our AI assistant. It will match you with businesses that have the part in stock. You can also search by category."
        }
      ]
    },
    {
      section: "For Buyers",
      icon: ShoppingCart,
      questions: [
        {
          q: "How do I contact the seller?",
          a: "Each part listing includes a “Message Seller” button. You can ask about availability, shipping, pickup, or payment."
        },
        {
          q: "How do I arrange pickup or delivery?",
          a: "Delivery or pickup is handled directly between you and the seller. We recommend confirming details like location, cost, and timeline in the chat before making a payment."
        },
        {
          q: "What payment methods do sellers accept?",
          a: "Sellers choose their own payment methods. These may include cash on pickup, PayPal, Zelle, Cash App, or bank transfer. Confirm the method directly with the seller before proceeding."
        },
        {
          q: "Is there a warranty on the parts?",
          a: "Warranty policies vary by seller. Please ask about warranty terms before purchasing. Some parts are sold as-is, others may include a 30-day or limited warranty."
        }
      ]
    },
    {
      section: "For Sellers",
      icon: Store,
      questions: [
        {
          q: "How do I list parts?",
          a: "After signing up, go to your dashboard and click “Add Part.” Fill in the vehicle details, part type, condition, price, and upload images."
        },
        {
          q: "Can I list multiple parts at once?",
          a: "Yes. Pro and Premium members can upload parts in bulk using our CSV tool or request upload support."
        },
        {
          q: "How do I get paid?",
          a: "You get paid directly by the customer based on the payment method you agree upon (e.g., Zelle, cash, PayPal)."
        },
        {
          q: "What happens if a buyer doesn't show up or pay?",
          a: "You’re free to report the buyer, block them, and repost the part. Repeat offenders may be suspended from the platform."
        }
      ]
    }
  ];

  return (
    <motion.div
      className="w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.h1
        className="text-3xl md:text-4xl py-2 font-bold mb-8 text-center bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text"
        variants={itemVariants}
      >
        Frequently Asked Questions
      </motion.h1>

      <div className="max-w-3xl mx-auto space-y-8">
        {faqData.map((sectionData, sectionIndex) => (
          <motion.div key={sectionIndex} variants={itemVariants}>
            <h2 className="flex items-center gap-2 text-2xl font-semibold mb-4 text-foreground">
              <sectionData.icon className="h-6 w-6 text-primary" />
              {sectionData.section}
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-card p-4 rounded-lg border shadow-sm"
            >
              {sectionData.questions.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${sectionIndex}-${index}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline text-base">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default FaqPage;
